import { useCallback } from "react";
import {
	ADVISOR_PERMISSIONS,
	GLOBAL_ADMIN_ROLES,
	PERMISSIONS,
	type Permission
} from "solution-advisor-application-services/rbac";

import { useSession } from "src/auth/useSession";

/**
 * What the caller may do in the active organization: the permissions `session.me` reports for their
 * role (Builder's roles; app/auth/rbac.ts). It only decides what the UI offers; the API checks every
 * call.
 */
export const usePermissions = () => {
	const { activeOrganization, user } = useSession();
	const permissions = activeOrganization?.permissions;
	const can = useCallback(
		(...required: Permission[]) => required.some(permission => permissions?.includes(permission) ?? false),
		[permissions]
	);
	const globalRole = user?.globalRole ?? null;
	const canOpenAdmin = can(
		PERMISSIONS.allOrganizationReaders,
		PERMISSIONS.securityRoleReaders,
		PERMISSIONS.securityRoleEditors,
		PERMISSIONS.locationCatalogEditors,
		PERMISSIONS.marketingResourceEditors,
		PERMISSIONS.featureFlagEditors,
		PERMISSIONS.apiTokenUsers,
		PERMISSIONS.apiTokenAdministrators
	);
	return {
		can,
		role: activeOrganization?.role,
		canEditCompanies: can(PERMISSIONS.companyEditors),
		canEditProjects: can(PERMISSIONS.projectEditors),
		canEditSolutions: can(PERMISSIONS.solutionEditors),
		canEditBriefcase: can(PERMISSIONS.briefcaseEditors),
		/**
		 * `workspace.start` creates a project, and workspace locations are project locations, so both
		 * need solution AND project editing (the API's `withAllPermissions`).
		 */
		canEditWorkspaceLocations: can(PERMISSIONS.solutionEditors) && can(PERMISSIONS.projectEditors),
		/**
		 * `solutions.generateBrief` / `generateServiceSummary` read the project and company and file the
		 * result in the briefcase.
		 */
		canGenerateBriefs:
			can(PERMISSIONS.solutionEditors) &&
			can(PERMISSIONS.projectReaders) &&
			can(PERMISSIONS.companyReaders) &&
			can(PERMISSIONS.briefcaseEditors),
		/** Overview's brief download generates a brief, then fetches it with `briefcase.downloadUrl`. */
		canDownloadBriefs:
			can(PERMISSIONS.solutionEditors) &&
			can(PERMISSIONS.projectReaders) &&
			can(PERMISSIONS.companyReaders) &&
			can(PERMISSIONS.briefcaseEditors) &&
			can(PERMISSIONS.briefcaseReaders),
		/** The AI advisor (chat and history): the same ADVISOR_PERMISSIONS the API checks. */
		canUseAdvisor: ADVISOR_PERMISSIONS.every(permission => can(permission)),
		/** `v1.notifications` (the bell, Activity): every notification type is about a solution. */
		canReadNotifications: can(PERMISSIONS.solutionReaders),
		canSeeMembers: can(PERMISSIONS.userReaders, PERMISSIONS.userEditors),
		canManageMembers: can(PERMISSIONS.userEditors),
		canUseApiTokens: can(PERMISSIONS.apiTokenUsers, PERMISSIONS.apiTokenAdministrators),
		canSeeRoles: can(PERMISSIONS.securityRoleReaders, PERMISSIONS.securityRoleEditors),
		canEditRoles: can(PERMISSIONS.securityRoleEditors),
		canSeeAllOrganizations: can(PERMISSIONS.allOrganizationReaders),
		/** `platform.catalogStatus`; "Sync now" (`platform.syncCatalog`) takes locationCatalog:editors alone. */
		canSeeLocationCatalog: can(PERMISSIONS.allOrganizationReaders, PERMISSIONS.locationCatalogEditors),
		canSyncLocationCatalog: can(PERMISSIONS.locationCatalogEditors),
		canEditMarketing: can(PERMISSIONS.marketingResourceEditors),
		canEditFeatureFlags: can(PERMISSIONS.featureFlagEditors),
		/** The admin console (/admin): any one of its pages. */
		canOpenAdmin,
		/** Builder's global admins (systemAdmin, dlrAdmin), who may enter any organization. */
		globalRole,
		isGlobalAdmin: globalRole !== null && (GLOBAL_ADMIN_ROLES as readonly string[]).includes(globalRole)
	};
};
